/* AssociateTaskDetail — sandboxed task view + 3 action buttons.
 * Strictly text fields, no images, no client name. */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, MapPin, AlertTriangle, FileText, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITY_AR = { riyadh: "الرياض", jeddah: "جدة", dammam: "الدمام" };

export default function AssociateTaskDetail() {
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // 'propose' | 'missing' | 'note' | null
  const [toast, setToast] = useState("");

  const token = () => localStorage.getItem("vta_token");
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/associate/tasks/${id}`, auth());
      setTask(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر تحميل المهمة");
    }
  };
  useEffect(() => { load(); }, [id]);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  if (err) return <div style={{ ...s.wrap, padding: 30 }}><div style={s.error}>{err}</div></div>;
  if (!task) return <div style={{ ...s.wrap, padding: 30, color: "#888" }}>جارٍ التحميل…</div>;

  return (
    <div style={s.wrap} data-testid="associate-task-detail">
      <div style={s.container}>
        <Link to="/associate/tasks" style={s.back}><ArrowRight size={14} /> العودة للمهام</Link>

        <header style={s.header}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 14 }}>
            <div>
              <div style={s.taskNum}>{task.task_number}</div>
              <h1 style={s.h1}>{task.country} — {task.visa_type || "—"}</h1>
            </div>
            <span style={s.statusPill}>{task.status_code}</span>
          </div>
          <div style={s.metaBar}>
            <span style={s.meta}><MapPin size={13} /> مدينة البصمة: <b style={{ color: "#d4af37" }}>{CITY_AR[task.biometric_city] || task.biometric_city}</b></span>
            <span style={s.meta}><Calendar size={13} /> السفر: {task.travel_date || "—"}</span>
            <span style={s.meta}><Calendar size={13} /> العودة: {task.return_date || "—"}</span>
            <span style={s.meta}>المتقدم: {task.client_initials}</span>
            <span style={s.meta}>النوع: {task.applicant_type}</span>
          </div>
        </header>

        {/* Members table — TEXT ONLY */}
        <section style={s.section}>
          <h2 style={s.h2}>المسافرون والمرافقون ({task.members_count})</h2>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th>الاسم</th><th>الجواز</th><th>الجنسية</th><th>الميلاد</th>
                  <th>الانتهاء</th><th>النوع</th><th>الإقامة/الهوية</th><th>العلاقة</th><th>وظيفة</th>
                </tr>
              </thead>
              <tbody>
                {task.members.map((m) => (
                  <tr key={m.id} style={m.is_companion ? s.companionRow : null}>
                    <td>{m.full_name}</td>
                    <td>{m.passport_number}</td>
                    <td>{m.nationality}</td>
                    <td>{m.date_of_birth}</td>
                    <td>{m.expiry_date}</td>
                    <td>{m.sex}</td>
                    <td>{m.national_id}</td>
                    <td>{m.relationship_to_main}{m.is_companion ? " · مرافق" : ""}</td>
                    <td>{m.job_title || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {task.sponsor_name && (
            <div style={s.sponsor}>👤 الكفيل / جهة العمل: <b>{task.sponsor_name}</b></div>
          )}
        </section>

        {/* Action buttons */}
        <section style={s.section}>
          <h2 style={s.h2}>الإجراءات المتاحة</h2>
          <div style={s.actions}>
            <button onClick={() => setModal("propose")} style={s.actBtn} data-testid="act-propose-btn">
              <Calendar size={16} /> اقتراح مواعيد
            </button>
            <button onClick={() => setModal("missing")} style={s.actBtn} data-testid="act-missing-btn">
              <AlertTriangle size={16} /> طلب نقص بيانات
            </button>
            <button onClick={() => setModal("note")} style={s.actBtn} data-testid="act-note-btn">
              <FileText size={16} /> ملاحظة تشغيلية
            </button>
            <button onClick={() => setModal("upload")} style={s.actBtn} data-testid="act-upload-btn">
              <Upload size={16} /> رفع إثبات الموعد
            </button>
          </div>
        </section>

        {toast && <div style={s.toast} data-testid="task-toast">{toast}</div>}
      </div>

      {modal === "propose" && <ProposeModal task={task} onClose={() => setModal(null)} onDone={(m) => { flash(m); load(); }} />}
      {modal === "missing" && <MissingModal task={task} onClose={() => setModal(null)} onDone={(m) => { flash(m); load(); }} />}
      {modal === "note" && <NoteModal task={task} onClose={() => setModal(null)} onDone={(m) => { flash(m); load(); }} />}
      {modal === "upload" && <UploadModal task={task} onClose={() => setModal(null)} onDone={(m) => { flash(m); load(); }} />}
    </div>
  );
}

// ─────────── Modals ───────────

function ProposeModal({ task, onClose, onDone }) {
  const [opts, setOpts] = useState([{ datetime: "", city: task.biometric_city || "riyadh", center_type: "" }]);
  const [mgr, setMgr] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const valid = opts.filter(o => o.datetime).map(o => ({
        datetime: new Date(o.datetime).toISOString(), city: o.city, center_type: o.center_type || null,
      }));
      if (!valid.length) { setErr("أضف موعداً واحداً على الأقل"); setBusy(false); return; }
      const token = localStorage.getItem("vta_token");
      await axios.post(`${API}/associate/tasks/${task.id}/propose-appointments`,
        { options: valid, manager_review_flag: mgr },
        { headers: { Authorization: `Bearer ${token}` } });
      onDone("تم اقتراح المواعيد بنجاح ✓");
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (typeof d === "object" && d?.code === "biometric_city_mismatch") {
        setErr(`المدينة لا تطابق مدينة البصمة المختارة (${d.expected_city}). فعّل علم المراجعة لو متعمد.`);
      } else {
        setErr(typeof d === "string" ? d : "تعذّر الإرسال");
      }
    } finally { setBusy(false); }
  };

  return (
    <Modal title="اقتراح مواعيد بصمة" onClose={onClose}>
      <p style={{ fontSize: 12, color: "#9a9a9f", marginBottom: 12 }}>
        مدينة البصمة المختارة: <b style={{ color: "#d4af37" }}>{CITY_AR[task.biometric_city]}</b>. أي اقتراح خارجها يحتاج علم المراجعة.
      </p>
      {opts.map((o, i) => (
        <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
          <input type="datetime-local" value={o.datetime}
                 onChange={e => setOpts(opts.map((x, j) => j === i ? { ...x, datetime: e.target.value } : x))}
                 style={s.input} data-testid={`propose-dt-${i}`} />
          <select value={o.city}
                  onChange={e => setOpts(opts.map((x, j) => j === i ? { ...x, city: e.target.value } : x))}
                  style={s.input} data-testid={`propose-city-${i}`}>
            <option value="riyadh">الرياض</option>
            <option value="jeddah">جدة</option>
            <option value="dammam">الدمام</option>
          </select>
        </div>
      ))}
      <button onClick={() => setOpts([...opts, { datetime: "", city: task.biometric_city || "riyadh", center_type: "" }])}
              style={s.smallBtn} data-testid="add-slot-btn">+ موعد آخر</button>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginTop: 10, color: "#bbb" }}>
        <input type="checkbox" checked={mgr} onChange={e => setMgr(e.target.checked)} data-testid="mgr-flag" />
        أحتاج مراجعة المدير (مدينة مختلفة عن البصمة).
      </label>
      {err && <div style={s.error}>{err}</div>}
      <div style={s.btnRow}>
        <button onClick={onClose} style={s.cancel}>إلغاء</button>
        <button onClick={submit} disabled={busy} style={s.primary} data-testid="submit-propose-btn">
          {busy ? "…" : "إرسال"}
        </button>
      </div>
    </Modal>
  );
}

function MissingModal({ task, onClose, onDone }) {
  const [field, setField] = useState("");
  const [desc, setDesc] = useState("");
  const [mid, setMid] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const token = localStorage.getItem("vta_token");
      await axios.post(`${API}/associate/tasks/${task.id}/request-missing`,
        { field_name: field, description_ar: desc, member_id: mid || null },
        { headers: { Authorization: `Bearer ${token}` } });
      onDone("تم رفع طلب النقص ✓"); onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإرسال");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="طلب نقص بيانات" onClose={onClose}>
      <label style={s.label}>الحقل الناقص</label>
      <input value={field} onChange={e => setField(e.target.value)} style={s.input} placeholder="مثال: passport_number" data-testid="missing-field" />
      <label style={s.label}>أي مسافر؟ (اختياري)</label>
      <select value={mid} onChange={e => setMid(e.target.value)} style={s.input} data-testid="missing-member">
        <option value="">— لا أحد محدد —</option>
        {task.members.map(m => <option key={m.id} value={m.id}>{m.full_name || m.passport_number}</option>)}
      </select>
      <label style={s.label}>الوصف</label>
      <textarea value={desc} onChange={e => setDesc(e.target.value)} style={{ ...s.input, height: 80 }} data-testid="missing-desc" />
      {err && <div style={s.error}>{err}</div>}
      <div style={s.btnRow}>
        <button onClick={onClose} style={s.cancel}>إلغاء</button>
        <button onClick={submit} disabled={busy || !field || !desc} style={s.primary} data-testid="submit-missing-btn">
          {busy ? "…" : "رفع للمكتب"}
        </button>
      </div>
    </Modal>
  );
}

function NoteModal({ task, onClose, onDone }) {
  const [text, setText] = useState("");
  const [sev, setSev] = useState("info");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const token = localStorage.getItem("vta_token");
      await axios.post(`${API}/associate/tasks/${task.id}/operational-note`,
        { text_ar: text, severity: sev },
        { headers: { Authorization: `Bearer ${token}` } });
      onDone("تم رفع الملاحظة ✓"); onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الإرسال");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="ملاحظة تشغيلية" onClose={onClose}>
      <label style={s.label}>المستوى</label>
      <select value={sev} onChange={e => setSev(e.target.value)} style={s.input} data-testid="note-severity">
        <option value="info">معلومة</option>
        <option value="warning">تحذير</option>
        <option value="blocker">مانع تشغيلي</option>
      </select>
      <label style={s.label}>نص الملاحظة</label>
      <textarea value={text} onChange={e => setText(e.target.value)} style={{ ...s.input, height: 90 }} data-testid="note-text" />
      {err && <div style={s.error}>{err}</div>}
      <div style={s.btnRow}>
        <button onClick={onClose} style={s.cancel}>إلغاء</button>
        <button onClick={submit} disabled={busy || text.length < 4} style={s.primary} data-testid="submit-note-btn">
          {busy ? "…" : "إرسال"}
        </button>
      </div>
    </Modal>
  );
}

function UploadModal({ task, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [conf, setConf] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) { setErr("اختر ملفاً"); return; }
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("confirmation_number", conf);
      const token = localStorage.getItem("vta_token");
      await axios.post(`${API}/associate/tasks/${task.id}/upload-appointment-proof`, fd,
        { headers: { Authorization: `Bearer ${token}` } });
      onDone("تم رفع إثبات الموعد ✓"); onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر الرفع");
    } finally { setBusy(false); }
  };

  return (
    <Modal title="رفع إثبات الموعد" onClose={onClose}>
      <label style={s.label}>الملف (PDF أو صورة)</label>
      <input type="file" accept="application/pdf,image/png,image/jpeg,image/webp"
             onChange={e => setFile(e.target.files?.[0] || null)} style={s.input} data-testid="upload-file" />
      <label style={s.label}>رقم تأكيد الموعد (اختياري)</label>
      <input value={conf} onChange={e => setConf(e.target.value)} style={s.input} data-testid="upload-conf" />
      {err && <div style={s.error}>{err}</div>}
      <div style={s.btnRow}>
        <button onClick={onClose} style={s.cancel}>إلغاء</button>
        <button onClick={submit} disabled={busy} style={s.primary} data-testid="submit-upload-btn">
          {busy ? "…" : "رفع"}
        </button>
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div style={s.modalBg} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHead}>
          <h3 style={{ margin: 0, color: "#d4af37" }}>{title}</h3>
          <button onClick={onClose} style={s.closeBtn} data-testid="modal-close">✕</button>
        </div>
        <div style={{ marginTop: 14 }}>{children}</div>
      </div>
    </div>
  );
}

const s = {
  wrap: { minHeight: "100vh", background: "#0a0a0f", color: "#e9e9ec", padding: "32px 16px",
          fontFamily: "'Tajawal', system-ui, sans-serif", direction: "rtl" },
  container: { maxWidth: 1080, margin: "0 auto" },
  back: { color: "#d4af37", fontSize: 12, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4, marginBottom: 16 },
  header: { background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 14, padding: 18, marginBottom: 16 },
  taskNum: { fontSize: 12, color: "#d4af37", fontWeight: 800, letterSpacing: 0.4 },
  h1: { fontSize: 22, fontWeight: 800, margin: "4px 0 0", color: "#fff" },
  statusPill: { fontSize: 11, padding: "4px 10px", background: "#1f2937", border: "1px solid #374151", borderRadius: 999, color: "#d1d5db" },
  metaBar: { marginTop: 14, display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: "#a3a3a8" },
  meta: { display: "inline-flex", alignItems: "center", gap: 4 },
  section: { background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 14, padding: 18, marginBottom: 16 },
  h2: { fontSize: 14, color: "#d4af37", margin: "0 0 12px", fontWeight: 700 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  companionRow: { background: "#1c1a10" },
  sponsor: { marginTop: 12, padding: 10, background: "#1a1810", borderRadius: 8, fontSize: 13, color: "#d4af37" },
  actions: { display: "flex", flexWrap: "wrap", gap: 10 },
  actBtn: { padding: "10px 14px", background: "#1f2937", border: "1px solid #374151", color: "#e9e9ec",
            borderRadius: 10, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 },
  modalBg: { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 100,
             display: "flex", alignItems: "center", justifyContent: "center", padding: 14 },
  modal: { background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 14, padding: 20,
           width: "100%", maxWidth: 520, color: "#e9e9ec" },
  modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { background: "transparent", color: "#888", border: "none", cursor: "pointer", fontSize: 18 },
  label: { display: "block", fontSize: 12, color: "#bbb", marginTop: 12, marginBottom: 4 },
  input: { width: "100%", padding: "9px 12px", background: "#0f0f17", border: "1px solid #2a2a3a",
           borderRadius: 8, color: "#fff", fontSize: 13, boxSizing: "border-box" },
  smallBtn: { padding: "6px 10px", background: "transparent", color: "#d4af37", border: "1px dashed #2a2a3a",
              borderRadius: 8, fontSize: 12, cursor: "pointer", marginTop: 4 },
  btnRow: { display: "flex", gap: 10, justifyContent: "flex-start", marginTop: 16 },
  cancel: { padding: "9px 16px", background: "transparent", color: "#bbb", border: "1px solid #2a2a3a", borderRadius: 8, cursor: "pointer" },
  primary: { padding: "9px 18px", background: "#d4af37", color: "#0a0a0f", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 800 },
  error: { padding: 10, background: "#3a1414", border: "1px solid #6b1f1f", borderRadius: 8, color: "#fca5a5", marginTop: 10, fontSize: 12 },
  toast: { position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
           background: "#10b981", color: "#0a0a0f", padding: "10px 16px", borderRadius: 10, fontWeight: 700 },
};
